// src/pages/Hypnogram.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import Plot from 'react-plotly.js';
import { NavigationContext } from '../context/NavigationContext';
import { useWarnings } from '../context/WarningsContext';
import { ThemeContext } from '../context/ThemeContext';
import DemoWalkthrough, { DontShowLink } from '../components/DemoWalkthrough';
import { shouldResume, setStep } from '../utils/walkthroughState';
import API_URL from '../config';

const Hypnogram = () => {
  const location = useLocation();
  const { recordingId } = useParams();
  const { openLevel } = useContext(NavigationContext);
  const { showWarnings } = useWarnings();
  const { isDarkMode } = useContext(ThemeContext);
  const [epochs, setEpochs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Capture demo-resume status at first render (before DemoWalkthrough clears the token).
  const [isDemoResume] = useState(() => shouldResume('hypnogram', recordingId));

  // Get the source of navigation (if any)
  const params = new URLSearchParams(location.search);
  const fromParam = params.get('from');

  useEffect(() => {
    fetch(`${API_URL}/api/epochs/?recording_id=${recordingId}`)
      .then((res) => res.json())
      .then((data) => {
        setEpochs(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching epochs:', err);
        setError(err);
        setLoading(false);
      });
  }, []);

  const handleGoToEpoch = (epochNumber) => {
    // Find the epoch with the matching epoch_number
    const epoch = epochs.find(e => e.epoch_number === epochNumber);
    if (!epoch) {
      console.error('Could not find epoch with number:', epochNumber);
      return;
    }

    openLevel({ 
      key: 'epoch', 
      label: `Epoch ${epoch.epoch_number}`, 
      route: `/analysis/${recordingId}/epoch/${epoch.id}${fromParam ? `?from=${encodeURIComponent(fromParam)}` : ''}`
    });
  };

  if (loading) {
    return <div className="p-6 text-gray-600 dark:text-gray-300">Loading hypnogram data...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-500 dark:text-red-400">Error loading data.</div>;
  }

  // Map sleep stage labels to numeric values for plotting
  const stageMapping = { 'Wake': 4, 'REM': 3, 'N1': 2, 'N2': 1, 'N3': 0 };
  
  // Create arrays for the main line segments
  const x = epochs.map((epoch) => new Date(epoch.epoch_start));
  const y = epochs.map((epoch) =>
    epoch.sleep_stage && stageMapping[epoch.sleep_stage] !== undefined
      ? stageMapping[epoch.sleep_stage]
      : null
  );

  // Create separate segments for continuous REM periods
  let remX = [];
  let remY = [];
  
  epochs.forEach((epoch, i) => {
    if (epoch.sleep_stage === 'REM') {
      // Add this REM point using timestamp
      remX.push(new Date(epoch.epoch_start));
      remY.push(stageMapping['REM']);
      
      // If this is the last epoch or next epoch is not REM, end this segment
      if (i === epochs.length - 1 || epochs[i + 1].sleep_stage !== 'REM') {
        remX.push(null);
        remY.push(null);
      }
    }
  });

  const commonAxisStyle = {
    visible: true,
    showline: false,
    gridcolor: '#374151', // gray-700
    zerolinecolor: '#4B5563', // gray-600
    showgrid: false,
  };

  const commonYAxisStyle = {
    ...commonAxisStyle,
    title: 'Sleep Stage',
    tickvals: [0, 1, 2, 3, 4],
    ticktext: ['N3', 'N2', 'N1', 'REM', 'Wake'],
    range: [-1, 4.5],
    fixedrange: true,
    zeroline: false,
  };

  // Create warning shapes
  const warningShapes = [
    // Baseline
    {
      type: 'line',
      x0: epochs.length > 0 ? new Date(epochs[0].epoch_start) : new Date(),
      x1: epochs.length > 0 ? new Date(epochs[epochs.length - 1].epoch_end) : new Date(),
      y0: -0.5,
      y1: -0.5,
      line: {
        color: '#4B5563',
        width: 1
      }
    }
  ];

  if (showWarnings) {
    // Create shapes for error warnings first
    let i = 0;
    while (i < epochs.length) {
      const epoch = epochs[i];
      if (epoch.error_warning) {
        // Find the end of this continuous error region
        let endIndex = i;
        while (endIndex + 1 < epochs.length && epochs[endIndex + 1].error_warning) {
          endIndex++;
        }
        
        // Add a single shape for this continuous region
        warningShapes.push({
          type: 'rect',
          x0: new Date(epochs[i].epoch_start),
          x1: new Date(epochs[endIndex].epoch_end),
          y0: -1,
          y1: 4.5,
          fillcolor: isDarkMode ? 'rgb(255, 85, 85)' : 'rgb(220, 38, 38)', // Vibrant red in dark mode, red-600 in light mode
          opacity: 0.4,
          line: { width: 0 },
          layer: 'below'
        });
        
        // Skip to after this region
        i = endIndex + 1;
      } else {
        i++;
      }
    }

    // Create shapes for disagreement warnings, but only where there's no error
    i = 0;
    while (i < epochs.length) {
      const epoch = epochs[i];
      if (epoch.disagreement_warning && !epoch.error_warning) {
        // Find the end of this continuous disagreement region
        let endIndex = i;
        while (endIndex + 1 < epochs.length && 
               epochs[endIndex + 1].disagreement_warning && 
               !epochs[endIndex + 1].error_warning) {
          endIndex++;
        }
        
        // Add a single shape for this continuous region
        warningShapes.push({
          type: 'rect',
          x0: new Date(epochs[i].epoch_start),
          x1: new Date(epochs[endIndex].epoch_end),
          y0: -1,
          y1: 4.5,
          fillcolor: isDarkMode ? 'rgb(255, 215, 0)' : 'rgb(234, 179, 8)', // Gold yellow in dark mode, yellow-500 in light mode
          opacity: 0.3,
          line: { width: 0 },
          layer: 'below'
        });
        
        // Skip to after this region
        i = endIndex + 1;
      } else {
        i++;
      }
    }

    // Create shapes for uncertainty warnings, but only where there's no error or disagreement
    i = 0;
    while (i < epochs.length) {
      const epoch = epochs[i];
      if (epoch.uncertainty_warning && !epoch.error_warning && !epoch.disagreement_warning) {
        // Find the end of this continuous uncertainty region
        let endIndex = i;
        while (endIndex + 1 < epochs.length && 
               epochs[endIndex + 1].uncertainty_warning && 
               !epochs[endIndex + 1].error_warning &&
               !epochs[endIndex + 1].disagreement_warning) {
          endIndex++;
        }
        
        // Add a single shape for this continuous region
        warningShapes.push({
          type: 'rect',
          x0: new Date(epochs[i].epoch_start),
          x1: new Date(epochs[endIndex].epoch_end),
          y0: -1,
          y1: 4.5,
          fillcolor: isDarkMode ? 'rgb(147, 51, 234)' : 'rgb(168, 85, 247)', // Bright purple in dark mode, purple-500 in light mode
          opacity: 0.3,
          line: { width: 0 },
          layer: 'below'
        });
        
        // Skip to after this region
        i = endIndex + 1;
      } else {
        i++;
      }
    }
  }

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-800">
      <DemoWalkthrough
        enabled={isDemoResume}
        steps={isDemoResume ? [
          {
            target: '#hypno-pattern',
            title: '🤖 Pattern-based Estimation',
            content: 'This hypnogram is generated by the ML model using signal patterns learned from large sleep datasets. It is currently under development.',
            placement: 'bottom',
            disableBeacon: true,
          },
          {
            target: '#hypno-rulebased',
            title: '📏 Rule-based Estimation',
            content: (
              <>This hypnogram follows the <strong>AASM scoring rules</strong> applied to EEG, EOG, and EMG channels. Coloured bands show areas of disagreement (yellow) or high uncertainty (purple). Click on any epoch in the hypnogram to inspect its raw signals.<DontShowLink /></>
            ),
            placement: 'top',
            disableBeacon: true,
            spotlightClicks: true,
            hideNextButton: true,
            onNext: () => {
              const n3Epoch = epochs.find(e => e.sleep_stage === 'N3') || epochs[0];
              if (n3Epoch) {
                setStep('epoch', recordingId);
                openLevel({
                  key: 'epoch',
                  label: `Epoch ${n3Epoch.epoch_number}`,
                  route: `/analysis/${recordingId}/epoch/${n3Epoch.id}`,
                });
              }
            },
          },
        ] : []}
      />
      {/* Pattern-based estimation */}
      <div id="hypno-pattern" className="mb-8 bg-white dark:bg-gray-900 rounded-lg p-6 shadow-sm">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Pattern-based Estimation</h2>
        <div className="border-b border-gray-200 dark:border-gray-700 mt-2 mb-4"></div>
        <div className="bg-white dark:bg-gray-900 rounded-lg p-4 mb-4">
          <Plot
            data={[
              {
                x: [],
                y: [],
                type: 'scatter',
                mode: 'lines',
                line: { color: '#10B981' }, // emerald-500
              },
            ]}
            layout={{
              title: 'Pattern-based Hypnogram (Not yet implemented)',
              paper_bgcolor: 'transparent',
              plot_bgcolor: 'transparent',
              font: { color: '#9CA3AF' },
              xaxis: { ...commonAxisStyle, title: 'Time', type: 'date', tickformat: '%H:%M' },
              yaxis: commonYAxisStyle,
              height: 250,
              margin: { t: 40, r: 20, l: 60, b: 40 }
            }}
            style={{ width: '100%' }}
            config={{ displayModeBar: false }}
          />
        </div>
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            This estimation uses machine learning patterns to identify sleep stages based on historical data and signal patterns.
            Currently under development.
          </p>
        </div>
      </div>

      {/* Rule-based estimation */}
      <div id="hypno-rulebased" className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-sm">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Rule-based Estimation</h2>
        </div>
        <div className="border-b border-gray-200 dark:border-gray-700 mt-2 mb-4"></div>
        <div className="bg-white dark:bg-gray-900 rounded-lg p-4 mb-4">
          <Plot
            data={[
              {
                x: x,
                y: y,
                type: 'scatter',
                mode: 'lines',
                line: { 
                  color: isDarkMode ? '#FFFFFF' : '#000000', 
                  width: 1 
                },
                showlegend: false
              },
              // Separate trace for REM stages with red color
              {
                x: remX,
                y: remY,
                type: 'scatter',
                mode: 'lines',
                line: { 
                  color: isDarkMode ? '#FF8080' : '#DC2626', // Light red in dark mode, darker red in light mode
                  width: 3 
                },
                showlegend: false
              }
            ]}
            layout={{
              title: 'Rule-based Hypnogram',
              paper_bgcolor: 'transparent',
              plot_bgcolor: 'transparent',
              font: { color: '#9CA3AF' },
              xaxis: { ...commonAxisStyle, title: 'Time', type: 'date', tickformat: '%H:%M' },
              yaxis: commonYAxisStyle,
              height: 250,
              margin: { t: 40, r: 20, l: 60, b: 40 },
              shapes: warningShapes,
              showlegend: false
            }}
            style={{ width: '100%' }}
            onClick={(event) => {
              if (event.points && event.points.length > 0) {
                const clickedTime = event.points[0].x;
                // Find the epoch with the closest start time to the clicked point
                const clickedEpoch = epochs.reduce((closest, current) => {
                  const currentDiff = Math.abs(new Date(current.epoch_start) - new Date(clickedTime));
                  const closestDiff = Math.abs(new Date(closest.epoch_start) - new Date(clickedTime));
                  return currentDiff < closestDiff ? current : closest;
                });
                
                if (clickedEpoch) {
                  // If the demo walkthrough is active, prime the epoch session token.
                  if (isDemoResume) setStep('epoch', recordingId);
                  handleGoToEpoch(clickedEpoch.epoch_number);
                }
              }
            }}
            config={{ displayModeBar: false }}
          />
        </div>
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            This estimation uses traditional rule-based algorithms to classify sleep stages based on established sleep scoring criteria.
            Click on any point in the hypnogram to view detailed epoch information.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Hypnogram;
